import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getSEOData } from '../utils/seoData';
import { STATE_EQUATION, ALGEBRA_OPERATIONS } from '../data/ewm';
import { EWM_IDEAS, EWM_VOCABULARY, SIDECAR_LOOP, SCENARIOS } from '../data/scenarios';
import './Narrative.css';

/**
 * Section 2 of the site — Scenarios.
 *
 * The hub carries the technical introduction to Emerging World Models (drawn
 * from the ewm-state-machine repository) and links to one page per scenario.
 */
function Scenarios() {
  return (
    <div className="np-page">
      <SEO {...getSEOData('scenarios')} />

      {/* Hero */}
      <section className="np-hero">
        <div className="container">
          <span className="np-eyebrow">Scenarios</span>
          <h1>Where a memory side-car is used, and what it measured</h1>
          <p className="np-hero-sub">
            Emerging World Models is a structural memory layer that sits beside a model and keeps
            its state. These are the cases it has been run against — vision, driving, generation,
            language, several models at once — each one traceable to a named notebook and each one
            published with its limits.
          </p>
          <div className="np-cta-row np-cta-row--center">
            <a href="#scenarios" className="np-btn np-btn--primary">Browse the scenarios</a>
            <Link to="/technology" className="np-btn np-btn--ghost">How the side-car works</Link>
          </div>
        </div>
      </section>

      {/* Technical introduction */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">What Emerging World Models actually is</h2>
          <p className="np-lead">
            Before the use cases, the mechanism. The whole framework rests on the state of the
            system and on one equation that says how a state follows from the state before it.
          </p>

          <div className="np-formula">{STATE_EQUATION.formula}</div>

          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr>
                  <th style={{ width: '140px' }}>Term</th>
                  <th>What it means</th>
                </tr>
              </thead>
              <tbody>
                {STATE_EQUATION.terms.map((t) => (
                  <tr key={t.symbol}>
                    <td><strong>{t.symbol}</strong></td>
                    <td>{t.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="np-source" style={{ marginTop: '18px' }}>
            Every term in that equation is a bit set, and every operation on them — union,
            intersection, difference, population count — is a bit operation. Tokens are restored
            only when the state is materialized, which is why the framework can talk about
            structure and about tokens without confusing the two.
          </p>
        </div>
      </section>

      {/* The four ideas */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">Four ideas, in order of depth</h2>
          <p className="np-lead">
            The model hangs on four ideas. Everything else — the loop, the guarantees, the
            scenarios below — is a consequence of them.
          </p>
          <div className="np-grid np-grid--2">
            {EWM_IDEAS.map((idea) => (
              <div key={idea.id} className="np-card">
                <span className="np-card-num">{idea.id}</span>
                <h3 className="np-h3">{idea.name}</h3>
                <p className="np-body">{idea.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The loop */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">The loop, end to end</h2>
          <p className="np-lead">
            One pass through the side-car. The loop is deliberately small, and it is the same loop
            for a language model, a vision encoder or a driving stack.
          </p>
          <div className="np-flow">
            {SIDECAR_LOOP.map((s) => (
              <div key={s.id} className="np-step">
                <div className="np-step-label">Step {s.id}</div>
                <div className="np-step-title">{s.step}</div>
                <p className="np-step-detail">{s.detail}</p>
              </div>
            ))}
          </div>
          <p className="np-source" style={{ marginTop: '22px' }}>
            The state lives outside the processing unit: a shareable cache holds the working state
            and the previous committed state, and a content-addressed store holds the committed
            stack whose top is the head. The unit itself owns only a handle to that store, which is
            why a crash costs a pop rather than a rebuild.
          </p>
        </div>
      </section>

      {/* What you can compute on a state */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">What can be computed on a state without materializing it</h2>
          <p className="np-lead">
            This is the practical difference between a store and an algebra: the interesting
            questions are answered on the bits, before any token is restored.
          </p>
          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr>
                  <th style={{ width: '200px' }}>Operation</th>
                  <th>What it gives you</th>
                </tr>
              </thead>
              <tbody>
                {ALGEBRA_OPERATIONS.map((a) => (
                  <tr key={a.op}>
                    <td><strong>{a.op}</strong></td>
                    <td>{a.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Vocabulary */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">The vocabulary used on these pages</h2>
          <p className="np-lead">
            Ten terms carry most of the meaning. They are worth having in one place before the
            scenarios, because the scenario pages do not re-explain them.
          </p>
          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr>
                  <th style={{ width: '160px' }}>Term</th>
                  <th>Meaning</th>
                </tr>
              </thead>
              <tbody>
                {EWM_VOCABULARY.map((v) => (
                  <tr key={v.term}>
                    <td><strong>{v.term}</strong></td>
                    <td>{v.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Scenario index */}
      <section className="np-section np-section--alt" id="scenarios">
        <div className="container">
          <h2 className="np-h2">The scenarios</h2>
          <p className="np-lead">
            Nine cases, drawn from sixteen notebooks. Each page states the host, the constraint,
            what was measured, and where the result is weaker than it looks.
          </p>
          <div className="np-grid np-grid--3">
            {SCENARIOS.map((s) => (
              <Link key={s.id} to={`/scenarios/${s.id}`} className="np-card np-card--link">
                <div className="np-card-icon">{s.icon}</div>
                <h3 className="np-h3">{s.name}</h3>
                <p className="np-body">{s.tagline}</p>
                <div className="np-badges">
                  {s.framework.slice(0, 2).map((f) => (
                    <span key={f} className="np-badge np-badge--muted">{f}</span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
          <div className="np-cta-row np-cta-row--center">
            <Link to="/evidence" className="np-btn np-btn--secondary">All measured results</Link>
            <Link to="/applications" className="np-btn np-btn--secondary">Applications by industry</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Scenarios;
